import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../utils/interfaces';
import './ProfileModal.css';

interface DistancesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile | null;
  onSave: (distances: DistanceData) => void;
}

interface DistanceData {
  driverDistance?: number;
  sevenIronDistance?: number;
  pitchingWedgeDistance?: number;
}

const DistancesModal: React.FC<DistancesModalProps> = ({
  isOpen,
  onClose,
  userProfile,
  onSave,
}) => {
  const [distances, setDistances] = useState<DistanceData>({
    driverDistance: undefined,
    sevenIronDistance: undefined,
    pitchingWedgeDistance: undefined,
  });

  useEffect(() => {
    if (userProfile) {
      setDistances({
        driverDistance: userProfile.driverDistance,
        sevenIronDistance: userProfile.sevenIronDistance,
        pitchingWedgeDistance: userProfile.pitchingWedgeDistance,
      });
    }
  }, [userProfile]);

  const handleSave = () => {
    onSave(distances);
    onClose();
  };

  const handleInputChange = (field: keyof DistanceData, value: string) => {
    const numValue = value === '' ? undefined : Number(value);
    setDistances(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Your Distances</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
            Enter your typical carry distances for these clubs. This helps us understand your swing speed and power.
          </p>

          <div className="profile-form">
            <div className="form-group">
              <label htmlFor="driver-distance" className="form-label">
                Driver Distance (yards)
              </label>
              <input
                id="driver-distance"
                type="number"
                placeholder="e.g., 220"
                value={distances.driverDistance || ''}
                onChange={(e) => handleInputChange('driverDistance', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="seven-iron-distance" className="form-label">
                7-Iron Distance (yards)
              </label>
              <input
                id="seven-iron-distance"
                type="number"
                placeholder="e.g., 130"
                value={distances.sevenIronDistance || ''}
                onChange={(e) => handleInputChange('sevenIronDistance', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pw-distance" className="form-label">
                Pitching Wedge Distance (yards)
              </label>
              <input
                id="pw-distance"
                type="number"
                placeholder="e.g., 100"
                value={distances.pitchingWedgeDistance || ''}
                onChange={(e) => handleInputChange('pitchingWedgeDistance', e.target.value)}
                className="form-input"
              />
            </div>

            <div style={{
              marginTop: '2rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              fontSize: '0.9rem',
              color: '#666'
            }}>
              <strong>💡 Tips:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                <li>Use your typical carry distances (not total with roll)</li>
                <li>You can leave fields empty if you're unsure</li>
                <li>These distances help us recommend clubs that match your swing speed</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="nf-btn nf-btn-secondary" 
            onClick={onClose}
            style={{ marginRight: '1rem' }}
          >
            Cancel
          </button>
          <button 
            className="nf-btn nf-btn-primary" 
            onClick={handleSave}
          >
            Save Distances
          </button>
        </div>
      </div>
    </div>
  );
};

export default DistancesModal;
